// src/components/PendentLoads.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../axiosConfig";
import {
  FaTimes,
  FaCheck,
  FaRegStickyNote,
} from "react-icons/fa";

import {
  Building2,
  Truck,
  MapPin,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import PendentNotesModal from "./PendentNotesModal";
import PendentLoadAcceptModal from "./PendentLoadAcceptModal";
import "../styles/PendentLoads.css";
import { getSocket } from "../lib/socket";

/* ===================== Helpers ===================== */
const formatCurrency = (v, { locale = "pt-PT", currency = "EUR" } = {}) => {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(n);
  } catch {
    return `${n.toFixed(2)} €`;
  }
};

const safeDate = (value, locale = "pt-PT") => {
  if (!value) return "";

  const d =
    new Date(
      value
    );

  if (
    Number.isNaN(
      d.getTime()
    )
  ) {
    return String(
      value
    );
  }

  try {
    return new Intl.DateTimeFormat(
      locale
    ).format(
      d
    );
  } catch {
    return d.toLocaleDateString(
      "pt-PT"
    );
  }
};



const COUNTRY_CODE_BY_NAME = {
  portugal:
    "PT",

  pt:
    "PT",

  espanha:
    "ES",

  spain:
    "ES",

  es:
    "ES",

  frança:
    "FR",

  franca:
    "FR",

  france:
    "FR",

  fr:
    "FR",

  alemanha:
    "DE",

  germany:
    "DE",

  de:
    "DE",

  itália:
    "IT",

  italia:
    "IT",

  italy:
    "IT",

  it:
    "IT",

  bélgica:
    "BE",

  belgica:
    "BE",

  belgium:
    "BE",

  be:
    "BE",

  holanda:
    "NL",

  "países baixos":
    "NL",

  "paises baixos":
    "NL",

  netherlands:
    "NL",

  nl:
    "NL",

  luxemburgo:
    "LU",

  luxembourg:
    "LU",

  lu:
    "LU",

  suíça:
    "CH",

  suica:
    "CH",

  switzerland:
    "CH",

  ch:
    "CH",

  áustria:
    "AT",

  austria:
    "AT",

  at:
    "AT",

  polónia:
    "PL",

  polonia:
    "PL",

  poland:
    "PL",

  pl:
    "PL",

  "reino unido":
    "GB",

  "united kingdom":
    "GB",

  gb:
    "GB",
};

const normalizeCountryName = (
  value
) =>
  String(
    value ||
      ""
  )
    .trim()
    .normalize(
      "NFD"
    )
    .replace(
      /[\u0300-\u036f]/g,
      ""
    )
    .toLowerCase();

const getCountryCode = (
  country
) => {
  const normalizedCountry =
    normalizeCountryName(
      country
    );

  const countryCode =
    COUNTRY_CODE_BY_NAME[
      normalizedCountry
    ] ||
    (
      /^[a-z]{2}$/i.test(
        normalizedCountry
      )
        ? normalizedCountry.toUpperCase()
        : ""
    );

  return String(
    countryCode ||
      ""
  )
    .trim()
    .toLowerCase();
};

const getFlagUrl = (
  country
) => {
  const countryCode =
    getCountryCode(
      country
    );

  if (
    !/^[a-z]{2}$/.test(
      countryCode
    )
  ) {
    return "";
  }

  return `https://flagcdn.com/w40/${countryCode}.png`;
};

const buildLocationDescription = ({
  postalCode,
  city,
  country,
}) => {
  const postalAndCity = [
    postalCode,
    city,
  ]
    .map(
      (
        value
      ) =>
        String(
          value ||
            ""
        ).trim()
    )
    .filter(
      Boolean
    )
    .join(
      " "
    );

  return [
    postalAndCity,
    String(
      country ||
        ""
    ).trim(),
  ]
    .filter(
      Boolean
    )
    .join(
      ", "
    );
};

const SHIPMENT_REQUEST_STATUS_LABELS = {
  WAITING_FOR_REPLIES:
    "A aguardar resposta",

  QUOTATIONS_RECEIVED:
    "Cotação enviada",

  NEEDS_APPROVAL:
    "A aguardar aprovação",
};

const getShipmentRequestStatusLabel = (
  status
) => {
  const normalizedStatus =
    String(
      status ||
        ""
    )
      .trim()
      .toUpperCase();

  return (
    SHIPMENT_REQUEST_STATUS_LABELS[
      normalizedStatus
    ] ||
    normalizedStatus ||
    "Pedido recebido"
  );
};

const getRowKey = (
  row
) => {
  if (
    row?.rowKey
  ) {
    return String(
      row.rowKey
    );
  }

  const sourceType =
    row?.sourceType ===
      "SHIPPER_REQUEST"
      ? "SHIPPER_REQUEST"
      : "PENDENT_LOAD";

  return `${sourceType}:${row?.id}`;
};

const isTrueFlag = (
  value
) => {
  if (
    value === true ||
    value === 1
  ) {
    return true;
  }

  const normalizedValue =
    String(
      value ??
        ""
    )
      .trim()
      .toLowerCase();

  return (
    normalizedValue ===
      "true" ||
    normalizedValue ===
      "1" ||
    normalizedValue ===
      "yes"
  );
};

const ACCEPT_OPTIONS_CACHE_PREFIX =
  "loadevo.pendentload.accept-options.v1";

const ACCEPT_OPTIONS_CACHE_MAX_AGE_MS =
  5 *
  60 *
  1000;

const getStoredAcceptOptionsToken =
  () => {
    if (
      typeof window ===
      "undefined"
    ) {
      return "";
    }

    return (
      window.localStorage.getItem(
        "token"
      ) ||
      window.localStorage.getItem(
        "authToken"
      ) ||
      window.sessionStorage.getItem(
        "token"
      ) ||
      window.sessionStorage.getItem(
        "authToken"
      ) ||
      ""
    );
  };

const decodeAcceptOptionsToken =
  (
    token
  ) => {
    try {
      const encoded =
        String(
          token ||
            ""
        )
          .split(
            "."
          )[1]
          ?.replace(
            /-/g,
            "+"
          )
          .replace(
            /_/g,
            "/"
          );

      if (
        !encoded
      ) {
        return null;
      }

      const padded =
        encoded +
        "=".repeat(
          (
            4 -
            (
              encoded.length %
              4
            )
          ) %
            4
        );

      return JSON.parse(
        window.atob(
          padded
        )
      );
    } catch {
      return null;
    }
  };

const resolveAcceptOptionsCacheScope =
  () => {
    const token =
      getStoredAcceptOptionsToken();

    const payload =
      token
        ? decodeAcceptOptionsToken(
            token
          )
        : null;

    const companyId =
      Number(
        payload?.company_id ??
          payload?.companyId
      );

    if (
      Number.isInteger(
        companyId
      ) &&
      companyId >
        0
    ) {
      return `company:${companyId}`;
    }

    if (
      token
    ) {
      return `token:${token.slice(
        -24
      )}`;
    }

    return null;
  };

const getAcceptOptionsCacheKey =
  (
    load
  ) => {
    if (
      !load
    ) {
      return null;
    }

    const scope =
      resolveAcceptOptionsCacheScope();

    if (
      !scope
    ) {
      return null;
    }

    const isShipmentRequest =
      String(
        load?.sourceType ||
          ""
      )
        .trim()
        .toUpperCase() ===
      "SHIPPER_REQUEST";

    const sourceType =
      isShipmentRequest
        ? "SHIPPER_REQUEST"
        : "PENDENT_LOAD";

    const targetId =
      isShipmentRequest
        ? load?.requestId ||
          load?.id
        : load?.id;

    if (
      targetId ===
        null ||
      targetId ===
        undefined ||
      targetId ===
        ""
    ) {
      return null;
    }

    const numericLength =
      Number(
        load?.length
      );

    const lengthKey =
      Number.isFinite(
        numericLength
      )
        ? numericLength.toFixed(
            2
          )
        : "no-length";

    return [
      ACCEPT_OPTIONS_CACHE_PREFIX,
      scope,
      sourceType,
      targetId,
      lengthKey,
    ].join(
      "."
    );
  };

const readAcceptOptionsCache =
  (
    load
  ) => {
    const cacheKey =
      getAcceptOptionsCacheKey(
        load
      );

    if (
      !cacheKey ||
      typeof window ===
        "undefined"
    ) {
      return null;
    }

    try {
      const raw =
        window.sessionStorage.getItem(
          cacheKey
        );

      if (
        !raw
      ) {
        return null;
      }

      const parsed =
        JSON.parse(
          raw
        );

      const savedAt =
        Number(
          parsed?.savedAt
        );

      if (
        !Number.isFinite(
          savedAt
        ) ||
        Date.now() -
          savedAt >
          ACCEPT_OPTIONS_CACHE_MAX_AGE_MS ||
        !Array.isArray(
          parsed?.sessions
        )
      ) {
        window.sessionStorage.removeItem(
          cacheKey
        );

        return null;
      }

      return parsed.sessions;
    } catch {
      try {
        window.sessionStorage.removeItem(
          cacheKey
        );
      } catch {}

      return null;
    }
  };

const writeAcceptOptionsCache =
  (
    load,
    sessions
  ) => {
    const cacheKey =
      getAcceptOptionsCacheKey(
        load
      );

    if (
      !cacheKey ||
      !Array.isArray(
        sessions
      ) ||
      typeof window ===
        "undefined"
    ) {
      return;
    }

    try {
      window.sessionStorage.setItem(
        cacheKey,
        JSON.stringify({
          savedAt:
            Date.now(),

          sessions,
        })
      );
    } catch (
      error
    ) {
      console.warn(
        "[PendentLoads] accept options cache write failed:",
        error?.message
      );
    }
  };

const clearAcceptOptionsCache =
  () => {
    if (
      typeof window ===
      "undefined"
    ) {
      return;
    }

    try {
      const keysToRemove =
        [];

      for (
        let index = 0;
        index <
        window.sessionStorage.length;
        index++
      ) {
        const key =
          window.sessionStorage.key(
            index
          );

        if (
          key?.startsWith(
            `${ACCEPT_OPTIONS_CACHE_PREFIX}.`
          )
        ) {
          keysToRemove.push(
            key
          );
        }
      }

      keysToRemove.forEach(
        (
          key
        ) => {
          window.sessionStorage.removeItem(
            key
          );
        }
      );
    } catch (
      error
    ) {
      console.warn(
        "[PendentLoads] accept options cache clear failed:",
        error?.message
      );
    }
  };

const SHARED_ACCEPT_SESSION_CACHE_PREFIX =
  "loadevo.pendentload.accept-session-snapshot.v1";

const getSharedAcceptSessionCacheKey =
  () => {
    const scope =
      resolveAcceptOptionsCacheScope();

    if (
      !scope
    ) {
      return null;
    }

    return `${SHARED_ACCEPT_SESSION_CACHE_PREFIX}.${scope}`;
  };

const readSharedAcceptSessionSnapshot =
  () => {
    const cacheKey =
      getSharedAcceptSessionCacheKey();

    if (
      !cacheKey ||
      typeof window ===
        "undefined"
    ) {
      return null;
    }

    try {
      const raw =
        window.sessionStorage.getItem(
          cacheKey
        );

      if (
        !raw
      ) {
        return null;
      }

      const parsed =
        JSON.parse(
          raw
        );

      /*
       * Shared TS cache uses stale-while-revalidate.
       *
       * Never throw away a structurally valid snapshot just because
       * some time passed. It gives us instant first paint, while
       * WebSocket + silent HTTP refresh it in the background.
       */
      if (
        !Array.isArray(
          parsed?.sessions
        )
      ) {
        window.sessionStorage.removeItem(
          cacheKey
        );

        return null;
      }

      return parsed.sessions;
    } catch {
      try {
        window.sessionStorage.removeItem(
          cacheKey
        );
      } catch {}

      return null;
    }
  };

const writeSharedAcceptSessionSnapshot =
  (
    sessions
  ) => {
    const cacheKey =
      getSharedAcceptSessionCacheKey();

    if (
      !cacheKey ||
      !Array.isArray(
        sessions
      ) ||
      typeof window ===
        "undefined"
    ) {
      return;
    }

    try {
      window.sessionStorage.setItem(
        cacheKey,
        JSON.stringify({
          savedAt:
            Date.now(),

          sessions,
        })
      );
    } catch (
      error
    ) {
      console.warn(
        "[PendentLoads] shared accept-session cache write failed:",
        error?.message
      );
    }
  };

const roundAcceptLength =
  (
    value
  ) =>
    Math.round(
      (
        Number(
          value
        ) ||
        0
      ) *
        100
    ) /
    100;

const buildAcceptOptionsFromSharedSnapshot =
  (
    load,
    sessions
  ) => {
    if (
      !Array.isArray(
        sessions
      )
    ) {
      return null;
    }

    const requestedLength =
      Number(
        load?.length
      );

    const hasValidRequestedLength =
      Number.isFinite(
        requestedLength
      ) &&
      requestedLength >
        0;

    return sessions.map(
      (
        session
      ) => {
        const remainingLength =
          roundAcceptLength(
            session
              ?.remainingLength
          );

        const normalizedRequestedLength =
          hasValidRequestedLength
            ? roundAcceptLength(
                requestedLength
              )
            : null;

        const canFit =
          hasValidRequestedLength &&
          normalizedRequestedLength <=
            remainingLength;

        return {
          ...session,

          requestedLength:
            normalizedRequestedLength,

          canFit,

          reason:
            !hasValidRequestedLength
              ? "LOAD_LENGTH_REQUIRED"
              : canFit
              ? null
              : "NOT_ENOUGH_SPACE",

          remainingAfterAcceptance:
            canFit
              ? roundAcceptLength(
                  remainingLength -
                    normalizedRequestedLength
                )
              : null,
        };
      }
    );
  };

/* ===================== Component ===================== */
const PendentLoads = ({ refreshLoads, searchTerm = "", showHeader = false }) => {
  const { t, i18n } = useTranslation("pendent_loads");
  const navigate = useNavigate();

const PENDENT_LOADS_CACHE_KEY =
  "loadevo.pendentloads.summary.v4";

  const [pendentLoads, setPendentLoads] = useState(() => {
    try {
      const raw = sessionStorage.getItem(PENDENT_LOADS_CACHE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const [loading, setLoading] = useState(() => {
    try {
      const raw = sessionStorage.getItem(PENDENT_LOADS_CACHE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return !Array.isArray(parsed) || parsed.length === 0;
    } catch {
      return true;
    }
  });

  const [error, setError] = useState("");
  const [isNotesOpen, setNotesOpen] = useState(false);
  const [selectedPendentLoad, setSelectedPendentLoad] = useState(null);
  const [notesDefaultValue, setNotesDefaultValue] = useState("");
  const [acceptingIds, setAcceptingIds] = useState({});
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [acceptModalLoad, setAcceptModalLoad] = useState(null);
  const [acceptOptions, setAcceptOptions] = useState([]);
  const [acceptOptionsLoading, setAcceptOptionsLoading] = useState(false);
  const [acceptOptionsError, setAcceptOptionsError] = useState("");
  const [acceptChoice, setAcceptChoice] = useState(null);
  const [deletingIds, setDeletingIds] = useState({});
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  const acceptModalLoadRef =
    useRef(
      null
    );

  const acceptOptionsRefreshTimerRef =
    useRef(
      null
    );

  const acceptOptionsRequestSerialRef =
    useRef(
      0
    );

  const acceptOptionsHasDataRef =
    useRef(
      false
    );

  const sharedAcceptSessionSnapshotRef =
    useRef(
      readSharedAcceptSessionSnapshot()
    );

  const sharedAcceptSessionRequestRef =
    useRef(
      null
    );

  const normalize =
    useCallback(
      (
        load
      ) => {
        const sourceType =
          load
            ?.sourceType ===
          "SHIPPER_REQUEST"
            ? "SHIPPER_REQUEST"
            : "PENDENT_LOAD";

        const normalizedLoad = {
          ...load,

          sourceType,

          carrierName:
            load
              ?.carrierName ??
            load
              ?.carrier
              ?.companyName ??
            load
              ?.transportCarrierName ??
            load
              ?.carrier_company_name ??
            null,
        };

        return {
          ...normalizedLoad,

          rowKey:
            getRowKey(
              normalizedLoad
            ),
        };
      },
      []
    );

  const loadPendentLoadsFromServer = useCallback(async ({ silent = false } = {}) => {
    if (!silent && pendentLoads.length === 0) {
      setLoading(true);
    }

    try {
      const { data } = await axios.get(`/pendentloads/summary`);
      const rows = (data?.loads || []).map((l) => normalize(l));

      /*
       * Hydrate the shared TS snapshot FIRST.
       *
       * This happens before setPendentLoads(rows), so React cannot render
       * a newly-fetched Pendent Load row before its modal capacity data
       * already exists in memory.
       */
      if (
        Array.isArray(
          data
            ?.acceptSessionSnapshot
            ?.sessions
        )
      ) {
        const sharedSessions =
          data
            .acceptSessionSnapshot
            .sessions;

        sharedAcceptSessionSnapshotRef.current =
          sharedSessions;

        writeSharedAcceptSessionSnapshot(
          sharedSessions
        );
      }

      setPendentLoads(rows);

      try {
        sessionStorage.setItem(PENDENT_LOADS_CACHE_KEY, JSON.stringify(rows));
      } catch {}

      setError("");

      try {
        sessionStorage.setItem(PENDENT_LOADS_CACHE_KEY, JSON.stringify(rows));
      } catch {}
    } catch (err) {
      console.error("Error fetching pendent loads:", err);
      setError(t("errors.fetchFailed"));
    } finally {
      setLoading(false);
    }
  }, [normalize, t, pendentLoads.length]);

  useEffect(() => {
    loadPendentLoadsFromServer({ silent: pendentLoads.length > 0 });
  }, [loadPendentLoadsFromServer, pendentLoads.length]);

  // 🔴 Live updates via WebSocket
  const fetchSharedAcceptSessionSnapshot =
    useCallback(
      async () => {
        /*
         * Deduplicate simultaneous refreshes.
         */
        if (
          sharedAcceptSessionRequestRef
            .current
        ) {
          return sharedAcceptSessionRequestRef
            .current;
        }

        const requestPromise =
          axios
            .get(
              "/pendentloads/accept-session-snapshot"
            )
            .then(
              (
                response
              ) => {
                const sessions =
                  Array.isArray(
                    response
                      ?.data
                      ?.sessions
                  )
                    ? response.data.sessions
                    : [];

                sharedAcceptSessionSnapshotRef.current =
                  sessions;

                writeSharedAcceptSessionSnapshot(
                  sessions
                );

                /*
                 * If a modal is already open, update it instantly
                 * from the fresh shared snapshot.
                 */
                const currentModalLoad =
                  acceptModalLoadRef
                    .current;

                if (
                  currentModalLoad
                ) {
                  const instantOptions =
                    buildAcceptOptionsFromSharedSnapshot(
                      currentModalLoad,
                      sessions
                    );

                  if (
                    instantOptions
                  ) {
                    acceptOptionsHasDataRef.current =
                      true;

                    setAcceptOptions(
                      instantOptions
                    );

                    setAcceptOptionsLoading(
                      false
                    );

                    setAcceptChoice(
                      (
                        previousChoice
                      ) => {
                        if (
                          previousChoice
                            ?.mode !==
                          "existing"
                        ) {
                          return previousChoice;
                        }

                        const selectedSession =
                          instantOptions.find(
                            (
                              session
                            ) =>
                              Number(
                                session.id
                              ) ===
                              Number(
                                previousChoice
                                  ?.transportsessionId
                              )
                          );

                        if (
                          !selectedSession ||
                          !selectedSession
                            .canFit
                        ) {
                          return null;
                        }

                        return previousChoice;
                      }
                    );
                  }
                }

                return sessions;
              }
            )
            .catch(
              (
                error
              ) => {
                console.warn(
                  "[PendentLoads] shared accept-session snapshot refresh failed:",
                  error?.message
                );

                return sharedAcceptSessionSnapshotRef
                  .current;
              }
            )
            .finally(
              () => {
                sharedAcceptSessionRequestRef.current =
                  null;
              }
            );

        sharedAcceptSessionRequestRef.current =
          requestPromise;

        return requestPromise;
      },
      []
    );

  /*
   * PREWARM:
   *
   * Run as soon as PendentLoads mounts.
   * By the time the dispatcher clicks a green check, the active
   * Transport Session snapshot is normally already in memory.
   */
  useEffect(
    () => {
      const cachedSnapshot =
        readSharedAcceptSessionSnapshot();

      if (
        cachedSnapshot
      ) {
        sharedAcceptSessionSnapshotRef.current =
          cachedSnapshot;
      }

      void fetchSharedAcceptSessionSnapshot();
    },
    [
      fetchSharedAcceptSessionSnapshot,
    ]
  );

  const fetchAcceptOptions =
    useCallback(
      async (
        load,
        {
          silent =
            false,
        } = {}
      ) => {
        if (
          !load
        ) {
          return [];
        }

        const isShipmentRequest =
          String(
            load?.sourceType ||
              ""
          )
            .trim()
            .toUpperCase() ===
          "SHIPPER_REQUEST";

        const targetId =
          isShipmentRequest
            ? load?.requestId ||
              load?.id
            : load?.id;

        if (
          targetId ===
            null ||
          targetId ===
            undefined ||
          targetId ===
            ""
        ) {
          return [];
        }

        const cacheKey =
          getAcceptOptionsCacheKey(
            load
          );

        const requestSerial =
          ++acceptOptionsRequestSerialRef
            .current;

        if (
          !silent
        ) {
          setAcceptOptionsLoading(
            true
          );

          setAcceptOptionsError(
            ""
          );
        }

        try {
          const endpoint =
            isShipmentRequest
              ? `/pendentloads/shipment-requests/${targetId}/accept-options`
              : `/pendentloads/${targetId}/accept-options`;

          const {
            data,
          } =
            await axios.get(
              endpoint
            );

          const sessions =
            Array.isArray(
              data?.sessions
            )
              ? data.sessions
              : [];

          writeAcceptOptionsCache(
            load,
            sessions
          );

          /*
           * Ignore an old HTTP response if another refresh was started
           * afterwards.
           */
          if (
            requestSerial !==
            acceptOptionsRequestSerialRef
              .current
          ) {
            return sessions;
          }

          const currentModalLoad =
            acceptModalLoadRef
              .current;

          if (
            !currentModalLoad ||
            getAcceptOptionsCacheKey(
              currentModalLoad
            ) !==
              cacheKey
          ) {
            return sessions;
          }

          acceptOptionsHasDataRef.current =
            true;

          setAcceptOptions(
            sessions
          );

          setAcceptOptionsError(
            ""
          );

          setAcceptOptionsLoading(
            false
          );

          /*
           * If another dispatcher filled the selected TS while this
           * modal was open, immediately remove the stale selection.
           */
          setAcceptChoice(
            (
              previousChoice
            ) => {
              if (
                previousChoice
                  ?.mode !==
                "existing"
              ) {
                return previousChoice;
              }

              const selectedSession =
                sessions.find(
                  (
                    session
                  ) =>
                    Number(
                      session.id
                    ) ===
                    Number(
                      previousChoice
                        ?.transportsessionId
                    )
                );

              if (
                !selectedSession ||
                !selectedSession.canFit
              ) {
                return null;
              }

              return previousChoice;
            }
          );

          return sessions;
        } catch (
          err
        ) {
          console.error(
            "❌ Error fetching Transport Session options:",
            err
          );

          if (
            requestSerial !==
            acceptOptionsRequestSerialRef
              .current
          ) {
            return [];
          }

          const currentModalLoad =
            acceptModalLoadRef
              .current;

          if (
            !currentModalLoad ||
            getAcceptOptionsCacheKey(
              currentModalLoad
            ) !==
              cacheKey
          ) {
            return [];
          }

          setAcceptOptionsLoading(
            false
          );

          /*
           * If we already have cached/live data, a temporary HTTP failure
           * should not blank the modal.
           */
          if (
            acceptOptionsHasDataRef
              .current
          ) {
            return [];
          }

          const backendError =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            t(
              "alerts.acceptOptionsFailed",
              "Não foi possível carregar as Transport Sessions ativas."
            );

          setAcceptOptionsError(
            backendError
          );

          return [];
        }
      },
      [
        t,
      ]
    );

  const openAcceptModal =
    (
      load
    ) => {
      if (
        !load
      ) {
        return;
      }

      const rowKey =
        getRowKey(
          load
        );

      if (
        acceptingIds[
          rowKey
        ]
      ) {
        return;
      }

      const modalLoad = {
        ...load,

        id:
          load?.id,

        requestId:
          load?.requestId ??
          null,

        sourceType:
          load?.sourceType ??
          "PENDENT_LOAD",

        pendentLoadNumber:
          load?.displayId ??
          load?.pendentLoadNumber ??
          load?.id,

        length:
          load?.length ??
          null,

        pickupLocation:
          load?.firstPickupLocation ??
          null,

        deliveryLocation:
          load?.firstDeliveryLocation ??
          null,

        latestPriceProposalId:
          load?.latestPriceProposalId ??
          null,

        latestPriceProposal:
          load?.latestPriceProposal ??
          null,
      };

      const cachedSessions =
        readAcceptOptionsCache(
          modalLoad
        );

      /*
       * An empty array is also a VALID shared snapshot:
       * it means there are currently zero active Transport Sessions.
       *
       * Therefore don't use ".length" to decide whether the snapshot
       * exists.
       */
      const inMemorySharedSnapshot =
        Array.isArray(
          sharedAcceptSessionSnapshotRef
            .current
        )
          ? sharedAcceptSessionSnapshotRef
              .current
          : null;

      const storedSharedSnapshot =
        inMemorySharedSnapshot ===
          null
          ? readSharedAcceptSessionSnapshot()
          : null;

      const sharedSnapshot =
        inMemorySharedSnapshot ??
        storedSharedSnapshot;

      const sharedInstantOptions =
        cachedSessions ===
          null &&
        Array.isArray(
          sharedSnapshot
        )
          ? buildAcceptOptionsFromSharedSnapshot(
              modalLoad,
              sharedSnapshot
            )
          : null;

      /*
       * Priority:
       *
       * 1. Exact per-load cache
       * 2. Shared Transport Session snapshot
       * 3. Cold HTTP only if neither exists
       */
      const instantSessions =
        cachedSessions !==
          null
          ? cachedSessions
          : sharedInstantOptions;

      acceptModalLoadRef.current =
        modalLoad;

      acceptOptionsRequestSerialRef.current +=
        1;

      setAcceptModalLoad(
        modalLoad
      );

      setAcceptOptionsError(
        ""
      );

      setAcceptChoice(
        null
      );

      if (
        instantSessions !==
        null
      ) {
        acceptOptionsHasDataRef.current =
          true;

        setAcceptOptions(
          instantSessions
        );

        setAcceptOptionsLoading(
          false
        );
      } else {
        acceptOptionsHasDataRef.current =
          false;

        setAcceptOptions(
          []
        );

        setAcceptOptionsLoading(
          true
        );
      }

      setAcceptModalOpen(
        true
      );

      /*
       * Exact load-specific endpoint still silently revalidates.
       * It remains more authoritative than the derived shared snapshot.
       */
      void fetchAcceptOptions(
        modalLoad,
        {
          silent:
            instantSessions !==
            null,
        }
      );
    };

  const closeAcceptModal =
    () => {
      /*
       * Invalidate any HTTP response still travelling for this modal.
       */
      acceptOptionsRequestSerialRef.current +=
        1;

      if (
        acceptOptionsRefreshTimerRef
          .current
      ) {
        window.clearTimeout(
          acceptOptionsRefreshTimerRef
            .current
        );

        acceptOptionsRefreshTimerRef.current =
          null;
      }

      acceptModalLoadRef.current =
        null;

      acceptOptionsHasDataRef.current =
        false;

      setAcceptModalOpen(
        false
      );

      setAcceptModalLoad(
        null
      );

      setAcceptOptions(
        []
      );

      setAcceptOptionsError(
        ""
      );

      setAcceptChoice(
        null
      );

      setAcceptOptionsLoading(
        false
      );
    };

  /*
   * Accept Modal realtime freshness.
   *
   * WebSocket tells us when load/session capacity may have changed.
   * The HTTP accept-options endpoint remains the authoritative source.
   *
   * Healthy WebSocket:
   *   no polling.
   *
   * Disconnected WebSocket:
   *   silent safety refresh every 5 minutes.
   */
  useEffect(
    () => {
      const socket =
        getSocket();

      const refreshCurrentAcceptModal =
        () => {
          const currentLoad =
            acceptModalLoadRef
              .current;

          if (
            !currentLoad
          ) {
            return;
          }

          if (
            acceptOptionsRefreshTimerRef
              .current
          ) {
            window.clearTimeout(
              acceptOptionsRefreshTimerRef
                .current
            );
          }

          acceptOptionsRefreshTimerRef.current =
            window.setTimeout(
              () => {
                acceptOptionsRefreshTimerRef.current =
                  null;

                const latestLoad =
                  acceptModalLoadRef
                    .current;

                if (
                  !latestLoad
                ) {
                  return;
                }

                void fetchAcceptOptions(
                  latestLoad,
                  {
                    silent:
                      true,
                  }
                );
              },
              140
            );
        };

      const invalidateAcceptOptionsAndRefresh =
        () => {
          /*
           * Exact per-load results are now stale.
           */
          clearAcceptOptionsCache();

          /*
           * Refresh the ONE shared Transport Session snapshot first.
           * This keeps every not-yet-opened modal warm too.
           */
          void fetchSharedAcceptSessionSnapshot()
            .finally(
              () => {
                /*
                 * Then revalidate the currently open modal using its
                 * exact load/request endpoint.
                 */
                refreshCurrentAcceptModal();
              }
            );
        };

      const subscribeToLoads =
        () => {
          socket.emit(
            "subscribe:loads"
          );
        };

      const handleSocketConnect =
        () => {
          subscribeToLoads();

          /*
           * Events may have been missed while the socket was offline.
           */
          clearAcceptOptionsCache();

          refreshCurrentAcceptModal();
        };

      const acceptModalRealtimeEvents = [
        "loads:rev",
        "loads:stale",
        "load:created",
        "load:updated",
        "load:deleted",
        "load:points:stale",
        "session:points",
        "session:points:stale",
        "session:route:updated",
        "pd:created",
        "pd:updated",
        "pd:deleted",
        "pd:bulk-driver",
        "pd:bulk-truck",
        "pd:bulk-trailer",
      ];

      socket.on(
        "connect",
        handleSocketConnect
      );

      socket.on(
        "reconnect",
        handleSocketConnect
      );

      acceptModalRealtimeEvents.forEach(
        (
          eventName
        ) => {
          socket.on(
            eventName,
            invalidateAcceptOptionsAndRefresh
          );
        }
      );

      if (
        socket.connected
      ) {
        subscribeToLoads();
      } else if (
        typeof socket.connect ===
        "function"
      ) {
        socket.connect();
      }

      const handleOnline =
        () => {
          clearAcceptOptionsCache();

          refreshCurrentAcceptModal();
        };

      const handleVisibilityChange =
        () => {
          if (
            document.visibilityState ===
            "visible"
          ) {
            refreshCurrentAcceptModal();
          }
        };

      window.addEventListener(
        "online",
        handleOnline
      );

      document.addEventListener(
        "visibilitychange",
        handleVisibilityChange
      );

      /*
       * Safety fallback only.
       *
       * When WebSocket is connected there is no periodic polling.
       */
      const acceptOptionsSafetyInterval =
        window.setInterval(
          () => {
            if (
              socket.connected
            ) {
              return;
            }

            const currentLoad =
              acceptModalLoadRef
                .current;

            if (
              !currentLoad
            ) {
              return;
            }

            void fetchAcceptOptions(
              currentLoad,
              {
                silent:
                  true,
              }
            );
          },
          5 *
            60 *
            1000
        );

      return () => {
        if (
          acceptOptionsRefreshTimerRef
            .current
        ) {
          window.clearTimeout(
            acceptOptionsRefreshTimerRef
              .current
          );

          acceptOptionsRefreshTimerRef.current =
            null;
        }

        window.clearInterval(
          acceptOptionsSafetyInterval
        );

        socket.off(
          "connect",
          handleSocketConnect
        );

        socket.off(
          "reconnect",
          handleSocketConnect
        );

        acceptModalRealtimeEvents.forEach(
          (
            eventName
          ) => {
            socket.off(
              eventName,
              invalidateAcceptOptionsAndRefresh
            );
          }
        );

        window.removeEventListener(
          "online",
          handleOnline
        );

        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      };
    },
    [
      fetchAcceptOptions,
      fetchSharedAcceptSessionSnapshot,
    ]
  );

  const confirmAccept =
    async () => {
      if (
        !acceptModalLoad ||
        !acceptChoice
      ) {
        return;
      }

      const isShipmentRequest =
        String(
          acceptModalLoad?.sourceType ||
            ""
        )
          .trim()
          .toUpperCase() ===
        "SHIPPER_REQUEST";

      const targetId =
        isShipmentRequest
          ? acceptModalLoad?.requestId ||
            acceptModalLoad?.id
          : acceptModalLoad?.id;

      if (
        !targetId
      ) {
        return;
      }

      const rowKey =
        getRowKey(
          acceptModalLoad
        );

      if (
        acceptingIds[
          rowKey
        ]
      ) {
        return;
      }

      const transportSessionMode =
        acceptChoice?.mode;

      const transportsessionId =
        transportSessionMode ===
          "existing"
          ? acceptChoice?.transportsessionId ||
            acceptChoice?.transportSessionId ||
            acceptChoice?.id ||
            null
          : null;

      if (
        transportSessionMode !==
          "new" &&
        transportSessionMode !==
          "existing"
      ) {
        return;
      }

      setAcceptingIds(
        (
          prev
        ) => ({
          ...prev,

          [rowKey]:
            true,
        })
      );

      try {
        const payload = {
          transportSessionMode,

          transportsessionId:
            transportSessionMode ===
              "existing"
              ? transportsessionId
              : null,
        };

        let data;

        if (
          isShipmentRequest
        ) {
          const proposalId =
            acceptModalLoad
              ?.latestPriceProposal
              ?.id ||
            acceptModalLoad
              ?.latestPriceProposalId ||
            (
              acceptModalLoad
                ?.priceNegotiationActions
                ?.canAcceptOriginalOffer
                ? "initial"
                : null
            );

          if (
            !proposalId
          ) {
            throw new Error(
              "There is no shipper offer available to accept."
            );
          }

          const response =
            await axios.patch(
              `/pendentloads/shipment-requests/${targetId}/price-proposals/${proposalId}/accept`,
              payload
            );

          data =
            response?.data ||
            {};
        } else {
          const response =
            await axios.post(
              `/pendentloads/${targetId}/accept`,
              payload
            );

          data =
            response?.data ||
            {};
        }

        setPendentLoads(
          (
            prev
          ) => {
            const next =
              prev.filter(
                (
                  load
                ) =>
                  getRowKey(
                    load
                  ) !==
                  rowKey
              );

            try {
              sessionStorage.setItem(
                PENDENT_LOADS_CACHE_KEY,
                JSON.stringify(
                  next
                )
              );
            } catch {}

            return next;
          }
        );

        if (
          typeof refreshLoads ===
          "function"
        ) {
          await refreshLoads(
            isShipmentRequest
              ? "shipment-request-accept"
              : "pendent-accept"
          );
        }

        /*
         * This acceptance changed one Transport Session's capacity.
         */
        clearAcceptOptionsCache();

        /*
         * Warm the shared snapshot immediately for the NEXT load the
         * dispatcher may accept.
         */
        void fetchSharedAcceptSessionSnapshot();

        void loadPendentLoadsFromServer({
          silent:
            true,
        });

        closeAcceptModal();

        window.alert(
          t(
            "alerts.acceptSuccess",
            {
              loadNumber:
                data?.load?.loadNumber ??
                data?.loadNumber ??
                data?.loadId ??
                "",
            }
          )
        );
      } catch (
        err
      ) {
        console.error(
          "❌ Error accepting load:",
          err
        );

        const backendError =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "";

        if (
          err?.response?.status ===
          409
        ) {
          setAcceptChoice(
            null
          );

          await fetchAcceptOptions(
            acceptModalLoad
          );

          window.alert(
            backendError ||
              t(
                "alerts.transportSessionChanged",
                "A capacidade desta Transport Session mudou. Escolhe novamente."
              )
          );
        } else if (
          err?.response?.status ===
          429
        ) {
          window.alert(
            backendError ||
              "Too many accept attempts. Please try again in a moment."
          );
        } else {
          window.alert(
            backendError ||
              t(
                "alerts.acceptFailed"
              )
          );
        }
      } finally {
        setAcceptingIds(
          (
            prev
          ) => {
            const next = {
              ...prev,
            };

            delete next[
              rowKey
            ];

            return next;
          }
        );
      }
    };

  const handleDelete = async (id) => {
    if (deletingIds[id]) return;

    const confirmDelete = window.confirm(t("confirmDelete"));
    if (!confirmDelete) return;

    setDeletingIds((prev) => ({ ...prev, [id]: true }));

    try {
      await axios.delete(`/pendentloads/${id}`);
      setPendentLoads((prev) =>
        prev.filter((load) => String(load.id) !== String(id))
      );
    } catch (err) {
      console.error("Error deleting pendent load:", err);

      if (err?.response?.status === 429) {
        setError(
          err?.response?.data?.error ||
            "Too many delete attempts. Please try again in a moment."
        );
      } else {
        setError(t("errors.deleteFailed"));
      }
    } finally {
      setDeletingIds((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const openNotesModal = async (load) => {
    try {
      const { data } = await axios.get(`/pendentloads/${load.id}`);
      const serverNote = typeof data?.notes === "string" ? data.notes : null;
      const local = localStorage.getItem(`pendentNote:${load.id}`) || "";
      setNotesDefaultValue((serverNote ?? local ?? "").trim());
    } catch {
      const local = localStorage.getItem(`pendentNote:${load.id}`) || "";
      setNotesDefaultValue(local.trim());
    }

    setSelectedPendentLoad({
      id: load.id,
      uiCounter: load.pendentLoadNumber ?? load.id,
    });
    setNotesOpen(true);
  };

  const closeNotesModal = () => {
    setNotesOpen(false);
    setSelectedPendentLoad(null);
    setNotesDefaultValue("");
  };

  const saveNotesFromModal = async (text) => {
    if (!selectedPendentLoad || isSavingNotes) return;

    setIsSavingNotes(true);

    const payload = { notes: (text ?? "").trim() };

    try {
      const { data } = await axios.put(
        `/pendentloads/${selectedPendentLoad.id}/notes`,
        payload
      );

      setPendentLoads((prev) =>
        prev.map((l) =>
          String(l.id) === String(selectedPendentLoad.id)
            ? { ...l, notes: data?.notes ?? null }
            : l
        )
      );
    } catch (e) {
      console.warn("Failed to save notes to server, keeping local draft:", e);

      if (e?.response?.status === 429) {
        window.alert(
          e?.response?.data?.error ||
            "Too many note updates. Please try again in a moment."
        );
      }
    } finally {
      const key = `pendentNote:${selectedPendentLoad.id}`;
      if (payload.notes) localStorage.setItem(key, payload.notes);
      else localStorage.removeItem(key);

      setIsSavingNotes(false);
      closeNotesModal();
    }
  };

  // 🔎 Filter: IDs, request reference, places, clients, status and price
  const filterPendentLoads =
    (
      list,
      search
    ) => {
      const term =
        String(
          search ||
            ""
        )
          .trim()
          .toLowerCase();

      if (
        !term
      ) {
        return list;
      }

      return list.filter(
        (
          load
        ) => {
          const identifiers = [
            load?.id,
            load?.pendentLoadNumber,
            load?.displayId,
            load?.reference,
            load?.rowKey,
          ]
            .filter(
              (
                value
              ) =>
                value !==
                  null &&
                value !==
                  undefined
            )
            .map(
              (
                value
              ) =>
                String(
                  value
                ).toLowerCase()
            );

          const textFields = [
            load
              ?.firstPickupLocation,

            load
              ?.firstDeliveryLocation,

                        load
              ?.firstPickupPostalCode,

            load
              ?.firstPickupCity,

            load
              ?.firstPickupCountry,

            load
              ?.firstDeliveryPostalCode,

            load
              ?.firstDeliveryCity,

            load
              ?.firstDeliveryCountry,

            load
              ?.industryClientName,

            load
              ?.carrierClientName,

            load
              ?.carrierName,

            load
              ?.shipperCompanyName,

            load
              ?.requestStatus,

            load
              ?.merchandise,

            load?.price !=
            null
              ? String(
                  load.price
                )
              : null,

            load?.price !=
            null
              ? formatCurrency(
                  load.price,
                  {
                    locale:
                      i18n.language ||
                      "pt-PT",
                  }
                )
              : null,
          ]
            .filter(
              Boolean
            )
            .map(
              (
                value
              ) =>
                String(
                  value
                ).toLowerCase()
            );

          return (
            identifiers.some(
              (
                identifier
              ) =>
                identifier.includes(
                  term
                )
            ) ||
            textFields.some(
              (
                field
              ) =>
                field.includes(
                  term
                )
            )
          );
        }
      );
    };

  const visibleLoads = filterPendentLoads(pendentLoads, searchTerm);

  const goToEditPendentLoad =
    (
      load
    ) => {
      const loadId =
        load?.id;

      const sourceType =
        String(
          load?.sourceType ||
            "PENDENT_LOAD"
        )
          .trim()
          .toUpperCase();

      if (
        loadId === null ||
        loadId === undefined ||
        loadId === ""
      ) {
        console.error(
          "[PendentLoads] The selected row has no ID:",
          load
        );

        return;
      }

      const isShipmentRequest =
        sourceType ===
        "SHIPPER_REQUEST";

      const editId =
        isShipmentRequest
          ? load?.requestId ||
            loadId
          : loadId;

      navigate(
        `/pendentloads/${editId}/edit${
          isShipmentRequest
            ? "?source=shipper-request"
            : ""
        }`,
        {
          state: {
            prefill:
              load,

            sourceType,

            requestId:
              isShipmentRequest
                ? editId
                : null,
          },
        }
      );
    };

  const renderDateHour = (
    dateStr,
    hour
  ) => {
    if (
      !dateStr
    ) {
      return t(
        "date.noDate"
      );
    }

    const formattedDate =
      safeDate(
        dateStr,
        i18n.language ||
          "pt-PT"
      );

    return `${formattedDate}${
      hour
        ? t(
            "date.atHour",
            {
              hour,
            }
          )
        : ""
    }`;
  };

  const renderLocationCell = ({
    location,
    postalCode,
    city,
    country,
    date,
    hour,
    count,
  }) => {
    if (
      !location
    ) {
      return t(
        "common.na"
      );
    }

    const locationDescription =
      buildLocationDescription({
        postalCode,
        city,
        country,
      });

    const countryFlagUrl =
      getFlagUrl(
        country
      );

    return (
      <div className="pendent-location-block">
        <div className="pendent-location-name-row">
          <span className="pendent-location-name">
            {location}
          </span>

          {Number(count) > 1 && (
            <span className="pendent-location-extra-badge">
              +{Number(count) - 1}
            </span>
          )}
        </div>

        {locationDescription && (
          <div className="pendent-location-meta">
            {countryFlagUrl && (
              <img
                className="pendent-location-flag"
                src={
                  countryFlagUrl
                }
                alt={
                  String(
                    country ||
                      ""
                  )
                }
                loading="lazy"
              />
            )}

            <MapPin
              className="pendent-location-pin"
              aria-hidden="true"
            />

            <span className="pendent-location-address">
              {locationDescription}
            </span>
          </div>
        )}

        <div className="location-date-hour">
          {renderDateHour(
            date,
            hour
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="pendent-loads">
      {showHeader && (
        <div className="pendent-header-card">
          <div className="pendent-header-left">
            <span className="pendent-pill">
              {t("header.total")}: <strong>{pendentLoads.length}</strong>
            </span>
            <span className="pendent-dot">•</span>
            <span className="pendent-pill">
              {t("header.showing")}: <strong>{visibleLoads.length}</strong>
            </span>
          </div>
          <div className="pendent-header-right" />
        </div>
      )}

      {error && <p className="pendent-error">{error}</p>}

      {loading ? (
        <div className="pendent-empty">
          <div className="pendent-empty__title">{t("empty.loadingTitle")}</div>
          <div className="pendent-empty__subtitle">{t("empty.loadingSubtitle")}</div>
        </div>
      ) : visibleLoads.length === 0 ? (
        <div className="pendent-empty">
          <div className="pendent-empty__title">{t("empty.noLoadsTitle")}</div>
          <div className="pendent-empty__subtitle">{t("empty.noLoadsSubtitle")}</div>
        </div>
      ) : (
        <div className="pendent-table">
          <div className="pendent-table-header">
            <div>{t("table.id")}</div>
            <div>{t("table.length")}</div>
            <div>{t("table.pickupPlace")}</div>
            <div>{t("table.deliveryPlace")}</div>
            <div>{t("table.client")}</div>
            <div>{t("table.carrier")}</div>
            <div>{t("table.status", "Estado")}</div>
            <div className="pendent-price-cell">{t("table.price")}</div>
            <div>{t("table.actions")}</div>
          </div>

          {visibleLoads.map((load) => {
            const isShipmentRequest =
              load.sourceType ===
              "SHIPPER_REQUEST";

            const canAcceptShipmentRequest =
              !isShipmentRequest ||
              Boolean(
                load
                  ?.priceNegotiationActions
                  ?.canAccept
              ) ||
              Boolean(
                load
                  ?.priceNegotiationActions
                  ?.canAcceptOriginalOffer
              );

            const uiId =
              load.displayId ??
              load.pendentLoadNumber ??
              load.id;

            const hasNotes =
              !isShipmentRequest &&
              Boolean(
                load.notes ||
                localStorage.getItem(
                  `pendentNote:${load.id}`
                )
              );

            const canOpenRow =
              load?.id !== null &&
              load?.id !== undefined &&
              load?.id !== "";

            const openRow =
              () => {
                if (
                  !canOpenRow
                ) {
                  return;
                }

                goToEditPendentLoad(
                  load
                );
              };

            return (
              <div
                key={
                  getRowKey(
                    load
                  )
                }
                className={[
                  "pendent-row",

                  canOpenRow
                    ? "clickable"
                    : "",

                  isShipmentRequest
                    ? "shipment-request-row"
                    : "",
                ]
                  .filter(
                    Boolean
                  )
                  .join(
                    " "
                  )}
                onClick={
                  canOpenRow
                    ? openRow
                    : undefined
                }
                tabIndex={
                  canOpenRow
                    ? 0
                    : undefined
                }
                role={
                  canOpenRow
                    ? "button"
                    : undefined
                }
                onKeyDown={
                  canOpenRow
                    ? (
                        event
                      ) => {
                        if (
                          event.key ===
                            "Enter" ||
                          event.key ===
                            " "
                        ) {
                          event.preventDefault();

                          openRow();
                        }
                      }
                    : undefined
                }
              >
                <div className="pendent-cell pendent-id-cell">
                  <span>
                    {uiId}
                  </span>

                  {isShipmentRequest && (
                    <span className="shipment-request-source-badge">
                      Pedido recebido
                    </span>
                  )}
                </div>

                <div className="pendent-cell">
                  <div>
                    {load.length !==
                      null &&
                    load.length !==
                      undefined &&
                    load.length !==
                      ""
                      ? `${load.length}m`
                      : "—"}
                  </div>
                </div>

                <div className="pendent-cell pendent-location-cell">
                  {renderLocationCell({
                    location:
                      load.firstPickupLocation,

                    postalCode:
                      load.firstPickupPostalCode,

                    city:
                      load.firstPickupCity,

                    country:
                      load.firstPickupCountry,

                    date:
                      load.firstPickupDate,

                    hour:
                      load.firstPickupHour,

                    count:
                      load.pickupCount,
                  })}
                </div>

                <div className="pendent-cell pendent-location-cell">
                  {renderLocationCell({
                    location:
                      load.firstDeliveryLocation,

                    postalCode:
                      load.firstDeliveryPostalCode,

                    city:
                      load.firstDeliveryCity,

                    country:
                      load.firstDeliveryCountry,

                    date:
                      load.firstDeliveryDate,

                    hour:
                      load.firstDeliveryHour,

                    count:
                      load.deliveryCount,
                  })}
                </div>

                {(load.loadBearer || load.specialLoad) && (
                  <div className="load-type-badge-floating-center">
                    {load.loadBearer && (
                      <span className="load-bearer-badge">
                        {t("badges.loadBearer")}
                      </span>
                    )}

                    {load.specialLoad && (
                      <span className="special-load-badge">
                        {t("badges.specialLoad")}
                      </span>
                    )}
                  </div>
                )}

                <div className="pendent-cell">
                  {load.industryClientName && (
                    <div className="client-info pendent-entity-info">
                      <span
                        className="pendent-entity-icon pendent-entity-icon--client"
                        title={t("tooltips.industryClient")}
                      >
                        <Building2
                          aria-hidden="true"
                        />
                      </span>

                      <span title={load.industryClientName}>
                        {load.industryClientName}
                      </span>
                    </div>
                  )}

                  {load.carrierClientName && (
                    <div className="client-info pendent-entity-info">
                      <span
                        className="pendent-entity-icon pendent-entity-icon--client"
                        title={t("tooltips.carrierClient")}
                      >
                        <Building2
                          aria-hidden="true"
                        />
                      </span>

                      <span title={load.carrierClientName}>
                        {load.carrierClientName}
                      </span>
                    </div>
                  )}

                  {!load.industryClientName &&
                    !load.carrierClientName &&
                    t("common.na")}
                </div>

                <div className="pendent-cell">
                  {load.carrierName ? (
                    <div className="client-info pendent-entity-info">
                      <span
                        className="pendent-entity-icon pendent-entity-icon--carrier"
                        title={
                          t(
                            "tooltips.transportCarrier"
                          )
                        }
                      >
                        <Truck
                          aria-hidden="true"
                        />
                      </span>

                      <span title={load.carrierName}>
                        {load.carrierName}
                      </span>
                    </div>
                  ) : (
                    t(
                      "common.na"
                    )
                  )}
                </div>

                <div className="pendent-cell pendent-status-cell">
                  {isShipmentRequest ? (
                    <div className="pendent-status-stack">
                      <span className="shipment-request-invited-badge">
                        Convidado
                      </span>

                      <span className="shipment-request-status-badge">
                        {getShipmentRequestStatusLabel(
                          load.requestStatus
                        )}
                      </span>
                    </div>
                  ) : (
                    t("common.na")
                  )}
                </div>

                <div className="pendent-cell pendent-price-cell">
                  {formatCurrency(load.price, { locale: i18n.language || "pt-PT" })}
                </div>

                <div className="pendent-cell pendent-actions-cell">
                  <div className="pendent-actions-wrap">
                    {!isShipmentRequest && (
                      <button
                        type="button"
                        className={[
                          "pendent-loads-note-inline-btn",

                          hasNotes
                            ? "pendent-loads-note-inline-btn--has-notes"
                            : "",
                        ]
                          .filter(
                            Boolean
                          )
                          .join(
                            " "
                          )}
                        title={
                          t(
                            "tooltips.notes"
                          )
                        }
                        aria-label={
                          t(
                            "aria.openNotes",
                            {
                              id:
                                uiId,
                            }
                          )
                        }
                        onMouseDown={
                          (
                            event
                          ) => {
                            event.stopPropagation();
                          }
                        }
                        onClick={
                          (
                            event
                          ) => {
                            event.stopPropagation();

                            openNotesModal(
                              load
                            );
                          }
                        }
                      >
                        <FaRegStickyNote />
                      </button>
                    )}

                    <button
                      type="button"
                      className="pendent-icon-btn danger"
                      title={
                        isShipmentRequest
                          ? "Recusar pedido"
                          : t(
                              "tooltips.delete"
                            )
                      }
                      aria-label={
                        isShipmentRequest
                          ? `Recusar pedido ${uiId}`
                          : t(
                              "aria.delete",
                              {
                                id:
                                  uiId,
                              }
                            )
                      }
                      disabled={
                        isShipmentRequest ||
                        Boolean(
                          deletingIds[
                            load.id
                          ]
                        )
                      }
                      onClick={
                        (
                          event
                        ) => {
                          event.stopPropagation();

                          if (
                            isShipmentRequest
                          ) {
                            return;
                          }

                          handleDelete(
                            load.id
                          );
                        }
                      }
                    >
                      <FaTimes />
                    </button>

                    <button
                      type="button"
                      className="pendent-icon-btn accept"
                      title={
                        isShipmentRequest
                          ? canAcceptShipmentRequest
                            ? "Aceitar proposta e criar carga"
                            : "Este pedido ainda não tem uma proposta do embarcador disponível para aceitar"
                          : t(
                              "tooltips.accept"
                            )
                      }
                      aria-label={
                        isShipmentRequest
                          ? `Aceitar pedido ${uiId}`
                          : t(
                              "aria.accept",
                              {
                                id:
                                  uiId,
                              }
                            )
                      }
                      disabled={
                        !canAcceptShipmentRequest ||
                        Boolean(
                          acceptingIds[
                            getRowKey(
                              load
                            )
                          ]
                        )
                      }
                      onClick={
                        (
                          event
                        ) => {
                          event.stopPropagation();

                          if (
                            !canAcceptShipmentRequest
                          ) {
                            return;
                          }

                          openAcceptModal(
                            load
                          );
                        }
                      }
                    >
                      <FaCheck />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

<PendentLoadAcceptModal
        isOpen={acceptModalOpen}
        load={acceptModalLoad}
        sessions={acceptOptions}
        loading={acceptOptionsLoading}
        error={acceptOptionsError}
        selectedChoice={acceptChoice}
        isSubmitting={
          Boolean(
            acceptModalLoad &&
              acceptingIds[
                getRowKey(
                  acceptModalLoad
                )
              ]
          )
        }
        onSelectChoice={setAcceptChoice}
        onClose={closeAcceptModal}
        onConfirm={confirmAccept}
      />

      <PendentNotesModal
        isOpen={isNotesOpen}
        pendentLoad={selectedPendentLoad}
        defaultValue={notesDefaultValue}
        onClose={() => setNotesOpen(false)}
        onSave={(text) => saveNotesFromModal(text)}
      />
    </div>
  );
};

export default PendentLoads;